import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import Translate from '@mui/icons-material/Translate';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { useTranslations } from '@/i18n/use-translations';
import { useLiveLanguageStatusQuery } from '@/state/queries/player/use-live-language-status';
import { useUpdateLiveLanguageMutation } from '@/state/mutations/player/use-update-live-language';
import { useClearLiveCaptionsMutation } from '@/state/mutations/player/use-clear-live-captions';
import {
  liveTranslationStageCaptionPatch,
  resolveLiveTranslationIconState,
  resolveLiveTranslationPanelView,
} from './live-translation-panel.helper';
import { performEqualSpreadIconButtonSx } from './perform-equal-spread-icon-row';

const STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  stopped: 'default',
  starting: 'info',
  running: 'success',
  waiting_for_audio: 'warning',
  degraded: 'error',
  stopping: 'default',
};

const ICON_COLOR = {
  unavailable: 'action.disabled',
  off: 'action.active',
  on: 'primary.main',
} as const;

export function LiveTranslationControlButton({ entityRef }: { entityRef: string }) {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const { hasAccess, isLoading: featureLoading } = useFeatureAccess('live-translation');
  const { data, isLoading, isError, isFetching, refetch } = useLiveLanguageStatusQuery(entityRef, {
    enabled: hasAccess,
  });
  const update = useUpdateLiveLanguageMutation();
  const clear = useClearLiveCaptionsMutation();

  const view = resolveLiveTranslationPanelView({
    featureLoading,
    hasAccess,
    hasStatus: data != null,
    statusError: isError,
    entitled: data?.entitled,
    killSwitchOff: data?.killSwitchOff,
    translationTemplateMissing: data?.translationTemplateMissing,
  });
  if (!view.visible) return null;

  const iconState = resolveLiveTranslationIconState({
    mode: view.mode,
    entitled: view.entitled,
    enabled: data?.enabled,
  });
  const title = t('flows.detail.perform.liveTranslation.title');
  const busy = update.isPending || clear.isPending || isLoading;
  const statusKey = data?.status ?? 'stopped';
  const statusLabel = t(`flows.detail.perform.liveTranslation.status.${statusKey}`, undefined, statusKey);
  const retryButton = (
    <Button color="inherit" size="small" disabled={isFetching} onClick={() => void refetch()}>
      {t('flows.detail.perform.liveTranslation.retry')}
    </Button>
  );

  return (
    <>
      <Tooltip title={title}>
        <IconButton
          size="medium"
          aria-label={title}
          aria-pressed={iconState === 'on'}
          onClick={() => setOpen(true)}
          sx={{ ...performEqualSpreadIconButtonSx, color: ICON_COLOR[iconState] }}
        >
          <Translate />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {view.mode !== 'ready' || !data ? (
            view.mode === 'error' ? (
              <Alert severity="error" action={retryButton}>
                {t('flows.detail.perform.liveTranslation.statusError')}
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('flows.detail.perform.liveTranslation.statusLoading')}
              </Typography>
            )
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {view.stale ? (
                <Alert severity="warning" action={retryButton}>
                  {t('flows.detail.perform.liveTranslation.statusStale')}
                </Alert>
              ) : null}
              {data.statusReason && statusKey === 'degraded' ? (
                <Typography variant="caption" color="error">
                  {data.statusReason}
                </Typography>
              ) : null}
              {!view.entitled ? (
                <Stack spacing={1}>
                  <Chip
                    size="small"
                    color={STATUS_COLOR[statusKey] ?? 'default'}
                    label={statusLabel}
                    sx={{ alignSelf: 'flex-start' }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('flows.detail.perform.liveTranslation.notEntitled')}
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={2} divider={<Divider flexItem />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <FormControlLabel
                      sx={{ ml: 0, mr: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={data.enabled === true}
                          disabled={busy}
                          onChange={(_, enabled) => update.mutate({ entityRef, enabled })}
                        />
                      }
                      label={t('flows.detail.perform.liveTranslation.enabled')}
                    />
                    <Chip size="small" color={STATUS_COLOR[statusKey] ?? 'default'} label={statusLabel} />
                  </Box>
                  <Stack>
                    <FormControlLabel
                      sx={{ ml: 0, mr: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={data.captions.stagePlayer !== false}
                          disabled={busy}
                          onChange={(_, stagePlayer) =>
                            update.mutate({ entityRef, ...liveTranslationStageCaptionPatch(stagePlayer) })
                          }
                        />
                      }
                      label={t('flows.detail.perform.liveTranslation.stage')}
                    />
                    <FormControlLabel
                      sx={{ ml: 0, mr: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={data.captions.onlinePlayer !== false}
                          disabled={busy}
                          onChange={(_, onlinePlayer) => update.mutate({ entityRef, captions: { onlinePlayer } })}
                        />
                      }
                      label={t('flows.detail.perform.liveTranslation.online')}
                    />
                    <FormControlLabel
                      sx={{ ml: 0, mr: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={data.showLanguageMarker === true}
                          disabled={busy}
                          onChange={(_, showLanguageMarker) => update.mutate({ entityRef, showLanguageMarker })}
                        />
                      }
                      label={t('flows.detail.perform.liveTranslation.marker')}
                    />
                  </Stack>
                  <Box>
                    <Button
                      size="small"
                      color="inherit"
                      disabled={busy || data.enabled !== true}
                      onClick={() => clear.mutate({ entityRef })}
                    >
                      {t('flows.detail.perform.liveTranslation.clear')}
                    </Button>
                  </Box>
                </Stack>
              )}
              {view.missingStage ? (
                <Alert severity="warning">{t('flows.detail.perform.liveTranslation.missingStage')}</Alert>
              ) : null}
              {view.missingOnline ? (
                <Alert severity="warning">{t('flows.detail.perform.liveTranslation.missingOnline')}</Alert>
              ) : null}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
